import { panCamera, rotateCamera, zoomCamera } from './camera.js';
import { rotate, screenToTile } from './projection.js';
import type { CameraState } from './projection.js';

type Point = [number, number];
type InputOptions = { onGestureEnd?: (state: CameraState) => void };

const controlledKey = (key: string): boolean => ['q', 'e', '+', '-', '=', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key);
const distance = ([x1, y1]: Point, [x2, y2]: Point): number => Math.hypot(x2 - x1, y2 - y1);
const midpoint = ([x1, y1]: Point, [x2, y2]: Point): Point => [(x1 + x2) / 2, (y1 + y2) / 2];

export function attachTycoonInput(
  target: HTMLElement,
  get: () => CameraState,
  set: (state: CameraState) => void,
  options: InputOptions = {},
): { destroy(): void; cancel(): void } {
  target.tabIndex = 0;
  const pointers = new Map<number, Point>();
  const keys = new Set<string>();
  let lastDrag: Point | undefined;
  let lastPinch: { distance: number; midpoint: Point } | undefined;
  let pointerGesture = false;
  let suppressClick = false;
  let wheelTimer: ReturnType<typeof setTimeout> | undefined;

  const point = (event: MouseEvent): Point => {
    const bounds = target.getBoundingClientRect();
    return [event.clientX - bounds.left, event.clientY - bounds.top];
  };
  const centered = ([x, y]: Point): Point => {
    const bounds = target.getBoundingClientRect();
    return [x - bounds.width / 2, y - bounds.height / 2];
  };
  const finish = () => options.onGestureEnd?.(get());
  const clearWheel = () => { if (wheelTimer !== undefined) clearTimeout(wheelTimer); wheelTimer = undefined; };
  const armWheel = () => {
    clearWheel();
    wheelTimer = setTimeout(() => { wheelTimer = undefined; finish(); }, 200);
  };
  const zoomBetween = (scale: number, from: Point, to: Point) => {
    const camera = get();
    const anchor = screenToTile(centered(from), camera);
    const next = zoomCamera(camera, scale);
    const shifted = screenToTile(centered(to), next);
    set(panCamera(next, anchor[0] - shifted[0], anchor[1] - shifted[1]));
  };
  const zoomAt = (scale: number, at: Point) => zoomBetween(scale, at, at);
  const cancel = () => {
    clearWheel();
    if (pointerGesture) suppressClick = true;
    pointers.clear(); lastDrag = undefined; lastPinch = undefined; pointerGesture = false; keys.clear();
  };
  const key = (event: KeyboardEvent) => {
    const name = event.key.toLowerCase();
    if (!controlledKey(name)) return;
    const camera = get(), step = 4 / camera.zoom;
    if (name === 'q' || name === 'e') set(rotateCamera(camera, name === 'q' ? -1 : 1));
    else if (name === '+' || name === '=') zoomAt(camera.zoom * 1.25, [target.getBoundingClientRect().width / 2, target.getBoundingClientRect().height / 2]);
    else if (name === '-') zoomAt(camera.zoom / 1.25, [target.getBoundingClientRect().width / 2, target.getBoundingClientRect().height / 2]);
    else if (name === 'arrowleft') set(panCamera(camera, -step, 0));
    else if (name === 'arrowright') set(panCamera(camera, step, 0));
    else if (name === 'arrowup') set(panCamera(camera, 0, -step));
    else set(panCamera(camera, 0, step));
    keys.add(name);
    event.preventDefault();
  };
  const keyup = (event: KeyboardEvent) => {
    const name = event.key.toLowerCase();
    if (!controlledKey(name)) return;
    if (keys.delete(name)) finish();
  };
  const down = (event: PointerEvent) => {
    const current = point(event);
    pointers.set(event.pointerId, current);
    target.setPointerCapture?.(event.pointerId);
    if (pointers.size === 1) lastDrag = current;
    if (pointers.size === 2) {
      const [first, second] = [...pointers.values()];
      if (!first || !second) return;
      lastPinch = { distance: distance(first, second), midpoint: midpoint(first, second) };
      pointerGesture = true;
    }
  };
  const move = (event: PointerEvent) => {
    if (!pointers.has(event.pointerId)) return;
    const current = point(event);
    pointers.set(event.pointerId, current);
    if (pointers.size >= 2) {
      const [first, second] = [...pointers.values()];
      if (!first || !second) return;
      const nextDistance = distance(first, second), nextMidpoint = midpoint(first, second);
      if (lastPinch && lastPinch.distance > 0) {
        zoomBetween(get().zoom * nextDistance / lastPinch.distance, lastPinch.midpoint, nextMidpoint);
      }
      lastPinch = { distance: nextDistance, midpoint: nextMidpoint };
      pointerGesture = true;
      return;
    }
    if (!lastDrag) { lastDrag = current; return; }
    const [previousX, previousY] = lastDrag;
    const camera = get(), dx = (current[0] - previousX) / (32 * camera.zoom), dy = (current[1] - previousY) / (16 * camera.zoom);
    lastDrag = current;
    if (dx === 0 && dy === 0) return;
    const local: Point = [-(dx + dy) / 2, (dx - dy) / 2];
    const world = rotate(local, ((4 - camera.rotation) % 4) as 0 | 1 | 2 | 3);
    set(panCamera(camera, world[0], world[1]));
    pointerGesture = true;
  };
  const up = (event: PointerEvent) => {
    if (!pointers.delete(event.pointerId)) return;
    target.releasePointerCapture?.(event.pointerId);
    if (pointers.size > 0) {
      const remaining = [...pointers.values()][0];
      lastDrag = remaining; lastPinch = undefined;
      return;
    }
    lastDrag = undefined; lastPinch = undefined;
    if (pointerGesture) { suppressClick = true; finish(); }
    pointerGesture = false;
  };
  const wheel = (event: WheelEvent) => {
    if (event.deltaY === 0) return;
    const camera = get();
    if (event.ctrlKey || event.metaKey) set(zoomCamera(camera, camera.zoom + (event.deltaY < 0 ? 1 : -1)));
    else zoomAt(camera.zoom * (event.deltaY < 0 ? 1.1 : 1 / 1.1), point(event));
    armWheel();
    event.preventDefault();
  };
  const click = (event: MouseEvent) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault(); event.stopImmediatePropagation();
  };
  const blur = () => cancel();
  target.addEventListener('keydown', key); target.addEventListener('keyup', keyup);
  target.addEventListener('pointerdown', down); target.addEventListener('pointermove', move);
  target.addEventListener('pointerup', up); target.addEventListener('pointercancel', cancel); target.addEventListener('lostpointercapture', cancel);
  target.addEventListener('wheel', wheel, { passive: false }); target.addEventListener('click', click, true);
  globalThis.addEventListener('blur', blur);
  return {
    cancel,
    destroy() {
      cancel();
      target.removeEventListener('keydown', key); target.removeEventListener('keyup', keyup);
      target.removeEventListener('pointerdown', down); target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up); target.removeEventListener('pointercancel', cancel); target.removeEventListener('lostpointercapture', cancel);
      target.removeEventListener('wheel', wheel); target.removeEventListener('click', click, true);
      globalThis.removeEventListener('blur', blur);
    },
  };
}
